import { useEffect, useState } from 'react'
import { useGetIdentity } from '@refinedev/core'
import { Link, useNavigate } from 'react-router-dom'
import {
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  List,
  Tag,
  Button,
  Space,
  Avatar,
  Spin,
  Empty,
  Alert,
  Badge,
} from 'antd'
import {
  TeamOutlined,
  MedicineBoxOutlined,
  CalendarOutlined,
  ExperimentOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  VideoCameraOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  ScheduleOutlined,
} from '@ant-design/icons'
import { StaffRole, ROLE_DISPLAY_NAMES } from '@/lib/permissions'
import { ScheduleConsultationModal } from '@/components/patients/ScheduleConsultationModal'

const { Title, Text } = Typography

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

interface UserIdentity {
  id: string
  name: string
  email: string
  role: StaffRole
  avatar?: string
}

interface QueueCounts {
  nurseQueue: number
  awaitingReview: number
  approved: number
  rejected: number
  shared: number
  total: number
}

interface QueueItem {
  id: string
  labName: string
  sampleDate: string
  status: string
  source: string
  createdAt: string
  hoursWaiting?: number
  isOverdue?: boolean
  user: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
    image: string | null
  }
}

interface ConsultationRequest {
  id: string
  workflowInstanceId: string
  workflowType: 'TRT_PATIENT' | 'VIDEO_CONSULTATION' | null
  isStandalone: boolean
  patient: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
    phone: string | null
    image: string | null
  }
  requestedAt: string
  dueAt: string | null
  isOverdue: boolean
  hoursWaiting: number
}

interface DoctorAppointment {
  id: string
  patientId: string
  patientName: string
  patientEmail: string
  patientImage: string | null
  scheduledAt: string
  duration: number
  status: string
  statusLabel: string
  roomUrl: string | null
  type: string
  typeLabel: string
}

interface RescheduleAppointment {
  id: string
  patient: {
    id: string
    name: string
    email: string
    phone: string | null
    image: string | null
  }
  doctor: {
    id: string
    name: string
    image: string | null
  }
  originalScheduledAt: string
  duration: number
  rescheduleReason: string | null
  flaggedAt: string
}

export default function DashboardPage() {
  const { data: identity, isLoading: identityLoading } = useGetIdentity<UserIdentity>()
  const navigate = useNavigate()
  const [counts, setCounts] = useState<QueueCounts | null>(null)
  const [nurseQueue, setNurseQueue] = useState<QueueItem[]>([])
  const [doctorQueue, setDoctorQueue] = useState<QueueItem[]>([])
  const [consultationRequests, setConsultationRequests] = useState<ConsultationRequest[]>([])
  const [doctorAppointments, setDoctorAppointments] = useState<DoctorAppointment[]>([])
  const [rescheduleAppointments, setRescheduleAppointments] = useState<RescheduleAppointment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showScheduleModal, setShowScheduleModal] = useState(false)

  const greeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Dobro jutro'
    if (hour < 18) return 'Dobar dan'
    return 'Dobro veče'
  }

  useEffect(() => {
    const fetchData = async () => {
      console.log('[Dashboard] identity:', identity)
      if (!identity?.role) {
        console.log('[Dashboard] No identity role, skipping fetch')
        return
      }
      console.log('[Dashboard] User role:', identity.role)
      setIsLoading(true)

      try {
        // Fetch lab result counts
        const countsRes = await fetch(`${API_URL}/admin/lab-results/counts`, {
          headers: getAuthHeaders(),
        })
        if (countsRes.ok) {
          const countsData = await countsRes.json()
          setCounts(countsData)
        }

        // Fetch role-specific data
        if (identity.role === 'NURSE' || identity.role === 'ADMIN') {
          // Nurse queue (lab results to verify)
          const queueRes = await fetch(`${API_URL}/admin/lab-results/queue?pageSize=5`, {
            headers: getAuthHeaders(),
          })
          if (queueRes.ok) {
            const queueData = await queueRes.json()
            setNurseQueue(queueData.data || [])
          }

          // Consultation requests (patients waiting for scheduling)
          console.log('[Dashboard] Fetching consultation requests...')
          const consultRes = await fetch(`${API_URL}/admin/consultations/pending?pageSize=5`, {
            headers: getAuthHeaders(),
          })
          console.log('[Dashboard] Consultations response status:', consultRes.status)
          if (consultRes.ok) {
            const consultData = await consultRes.json()
            console.log('[Dashboard] Consultations data:', consultData)
            setConsultationRequests(consultData.data || [])
          } else {
            console.error('[Dashboard] Failed to fetch consultations:', await consultRes.text())
          }

          // Appointments needing reschedule (flagged by doctors)
          const rescheduleRes = await fetch(`${API_URL}/admin/consultations/needs-reschedule?pageSize=5`, {
            headers: getAuthHeaders(),
          })
          if (rescheduleRes.ok) {
            const rescheduleData = await rescheduleRes.json()
            setRescheduleAppointments(rescheduleData.data || [])
          }
        }

        if (identity.role === 'DOCTOR' || identity.role === 'ADMIN') {
          // Doctor's assigned reviews
          const reviewRes = await fetch(
            `${API_URL}/admin/lab-results?status=AWAITING_REVIEW&pageSize=5`,
            { headers: getAuthHeaders() }
          )
          if (reviewRes.ok) {
            const reviewData = await reviewRes.json()
            setDoctorQueue(reviewData.data || [])
          }
        }

        if (identity.role === 'DOCTOR') {
          // Doctor's upcoming appointments
          const appointmentsRes = await fetch(`${API_URL}/admin/consultations/my`, {
            headers: getAuthHeaders(),
          })
          if (appointmentsRes.ok) {
            const appointmentsData = await appointmentsRes.json()
            // Filter to only scheduled/confirmed, sort by date, take first 5
            const upcoming = appointmentsData
              .filter((apt: DoctorAppointment) => ['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS'].includes(apt.status))
              .sort((a: DoctorAppointment, b: DoctorAppointment) =>
                new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
              )
              .slice(0, 5)
            setDoctorAppointments(upcoming)
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [identity?.role])

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('sr-Latn-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('sr-Latn-RS', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Classify an appointment's time into one of three actionable states:
   *  - 'upcoming'   — future, not yet joinable
   *  - 'live'       — within scheduled window (incl. 15min doctor early-join
   *                   + duration + 15min grace) — doctor should be able to
   *                   enter the call
   *  - 'past'       — window is over; video poziv is disabled
   *
   * Doctor can join from 15 minutes before scheduledAt (matches the
   * server-side join-token early-access buffer).
   */
  const getTimeRemaining = (dateStr: string, durationMin: number = 30) => {
    const now = new Date()
    const scheduledAt = new Date(dateStr)
    const joinStart = new Date(scheduledAt.getTime() - 15 * 60 * 1000)
    const joinEnd = new Date(scheduledAt.getTime() + (durationMin + 15) * 60 * 1000)

    const diffMs = scheduledAt.getTime() - now.getTime()

    if (now >= joinStart && now <= joinEnd) {
      return { text: 'U toku', color: 'green' as const, state: 'live' as const }
    }
    if (now > joinEnd) {
      return { text: 'Prošlo', color: 'default' as const, state: 'past' as const }
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMinutes < 60) {
      return { text: `za ${diffMinutes} min`, color: 'red' as const, state: 'upcoming' as const }
    }
    if (diffHours < 24) {
      const mins = diffMinutes % 60
      return {
        text: `za ${diffHours}h ${mins}min`,
        color: diffHours < 2 ? 'orange' as const : 'blue' as const,
        state: 'upcoming' as const,
      }
    }
    if (diffDays === 1) {
      return { text: 'sutra', color: 'green' as const, state: 'upcoming' as const }
    }
    if (diffDays < 7) {
      return { text: `za ${diffDays} dana`, color: 'default' as const, state: 'upcoming' as const }
    }
    const diffMonths = Math.floor(diffDays / 30)
    if (diffMonths > 0) {
      return { text: `za ${diffMonths} mes.`, color: 'default' as const, state: 'upcoming' as const }
    }
    return { text: `za ${diffDays} dana`, color: 'default' as const, state: 'upcoming' as const }
  }

  const getPatientName = (user: QueueItem['user']) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim()
    }
    return user.email
  }

  if (identityLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  const isNurse = identity?.role === 'NURSE'
  const isDoctor = identity?.role === 'DOCTOR'
  const isAdmin = identity?.role === 'ADMIN'

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          {greeting()}, {identity?.name?.split(' ')[0] || 'User'}
        </Title>
        <Text type="secondary">
          {identity?.role ? ROLE_DISPLAY_NAMES[identity.role] : ''} - Kontrolna tabla
        </Text>
      </div>

      {/* Quick Actions */}
      {(isNurse || isAdmin) && (
        <Card title="Brze akcije" style={{ marginBottom: 16 }}>
          <Space wrap>
            <Button
              icon={<VideoCameraOutlined />}
              onClick={() => setShowScheduleModal(true)}
            >
              Zakaži video konsultaciju
            </Button>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => navigate('/consultations')}
            >
              Zahtevi za konsultaciju
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => navigate('/patients')}
            >
              Pregled pacijenata
            </Button>
          </Space>
        </Card>
      )}

      {/* Stats Grid */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {(isNurse || isAdmin) && (
          <Col xs={24} sm={12} lg={6}>
            <Link to="/lab-results">
              <Card hoverable>
                <Statistic
                  title="Čeka verifikaciju"
                  value={counts?.nurseQueue || 0}
                  prefix={<ExperimentOutlined style={{ color: '#1677ff' }} />}
                  valueStyle={{ color: counts?.nurseQueue ? '#1677ff' : undefined }}
                />
              </Card>
            </Link>
          </Col>
        )}
        {(isDoctor || isAdmin) && (
          <Col xs={24} sm={12} lg={6}>
            <Link to="/lab-results?status=AWAITING_REVIEW">
              <Card hoverable>
                <Statistic
                  title="Čeka pregled lekara"
                  value={counts?.awaitingReview || 0}
                  prefix={<MedicineBoxOutlined style={{ color: '#faad14' }} />}
                  valueStyle={{ color: counts?.awaitingReview ? '#faad14' : undefined }}
                />
              </Card>
            </Link>
          </Col>
        )}
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Odobreno"
              value={counts?.approved || 0}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ukupno rezultata"
              value={counts?.total || 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Role-specific sections */}
      <Row gutter={[24, 24]}>
        {/* Nurse Section: Lab Results Queue */}
        {(isNurse || isAdmin) && (
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <ExperimentOutlined />
                  <span>Red za verifikaciju</span>
                  {counts?.nurseQueue ? (
                    <Badge count={counts.nurseQueue} style={{ backgroundColor: '#1677ff' }} />
                  ) : null}
                </Space>
              }
              extra={
                <Link to="/lab-results">
                  <Button type="link" icon={<ArrowRightOutlined />}>
                    Pogledaj sve
                  </Button>
                </Link>
              }
            >
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <Spin />
                </div>
              ) : nurseQueue.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Nema rezultata na čekanju"
                />
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={nurseQueue}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Link to={`/lab-results/${item.id}`} key="verify">
                          <Button type="primary" size="small">
                            Pregledaj
                          </Button>
                        </Link>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            src={item.user.image}
                            icon={!item.user.image && <UserOutlined />}
                            style={{ backgroundColor: '#1677ff' }}
                          />
                        }
                        title={
                          <Link to={`/lab-results/${item.id}`}>
                            {getPatientName(item.user)}
                          </Link>
                        }
                        description={
                          <Space size="small">
                            <Text type="secondary">{item.labName}</Text>
                            {item.hoursWaiting !== undefined && (
                              <Tag
                                icon={<ClockCircleOutlined />}
                                color={item.isOverdue ? 'error' : item.hoursWaiting >= 24 ? 'warning' : 'default'}
                              >
                                {item.hoursWaiting}h
                              </Tag>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </Col>
        )}

        {/* Nurse Section: Consultation Requests */}
        {(isNurse || isAdmin) && (
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <PhoneOutlined />
                  <span>Zahtevi za konsultaciju</span>
                </Space>
              }
              extra={
                <Link to="/consultations">
                  <Button type="link" icon={<ArrowRightOutlined />}>
                    Pogledaj sve
                  </Button>
                </Link>
              }
            >
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <Spin />
                </div>
              ) : consultationRequests.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Nema zahteva za konsultaciju"
                />
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={consultationRequests}
                  renderItem={(item) => {
                    const patientName = item.patient.firstName || item.patient.lastName
                      ? `${item.patient.firstName || ''} ${item.patient.lastName || ''}`.trim()
                      : item.patient.email
                    return (
                      <List.Item
                        actions={[
                          <Link to={`/consultations/${item.id}`} key="schedule">
                            <Button size="small">Zakaži</Button>
                          </Link>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              src={item.patient.image}
                              icon={!item.patient.image && <UserOutlined />}
                              style={{ backgroundColor: '#722ed1' }}
                            />
                          }
                          title={
                            <Space>
                              {patientName}
                              {item.isStandalone ? (
                                <Tag color="purple" style={{ marginLeft: 8 }}>Samostalna</Tag>
                              ) : item.workflowType === 'TRT_PATIENT' ? (
                                <Tag color="blue" style={{ marginLeft: 8 }}>TRT</Tag>
                              ) : null}
                              {item.isOverdue && (
                                <Tag color="error" style={{ marginLeft: 8 }}>Kasni</Tag>
                              )}
                            </Space>
                          }
                          description={
                            <Space size="small">
                              <Text type="secondary">
                                {formatDate(item.requestedAt)}
                              </Text>
                              {item.patient.phone && (
                                <>
                                  <Text type="secondary">•</Text>
                                  <PhoneOutlined style={{ color: '#8c8c8c' }} />
                                  <Text type="secondary">{item.patient.phone}</Text>
                                </>
                              )}
                            </Space>
                          }
                        />
                      </List.Item>
                    )
                  }}
                />
              )}
            </Card>
          </Col>
        )}

        {/* Nurse Section: Appointments Needing Reschedule */}
        {(isNurse || isAdmin) && rescheduleAppointments.length > 0 && (
          <Col xs={24}>
            <Card
              title={
                <Space>
                  <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                  <span>Potrebno ponovo zakazati</span>
                  <Badge count={rescheduleAppointments.length} style={{ backgroundColor: '#faad14' }} />
                </Space>
              }
              style={{ borderColor: '#faad14' }}
            >
              <List
                itemLayout="horizontal"
                dataSource={rescheduleAppointments}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Link to={`/consultations?patientId=${item.patient.id}`} key="reschedule">
                        <Button type="primary" size="small" icon={<ScheduleOutlined />}>
                          Zakaži ponovo
                        </Button>
                      </Link>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          src={item.patient.image}
                          icon={!item.patient.image && <UserOutlined />}
                          style={{ backgroundColor: '#faad14' }}
                        />
                      }
                      title={
                        <Space>
                          {item.patient.name}
                          <Text type="secondary" style={{ fontWeight: 'normal' }}>
                            (Lekar: {item.doctor.name})
                          </Text>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Space size="small">
                            <CalendarOutlined style={{ color: '#8c8c8c' }} />
                            <Text type="secondary">
                              Prethodni termin: {formatDateTime(item.originalScheduledAt)}
                            </Text>
                          </Space>
                          {item.rescheduleReason && (
                            <Text type="secondary" style={{ fontStyle: 'italic' }}>
                              Razlog: {item.rescheduleReason}
                            </Text>
                          )}
                          {item.patient.phone && (
                            <Space size="small">
                              <PhoneOutlined style={{ color: '#8c8c8c' }} />
                              <a href={`tel:${item.patient.phone}`}>{item.patient.phone}</a>
                            </Space>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        )}

        {/* Doctor Section: Assigned Reviews */}
        {(isDoctor || isAdmin) && (
          <Col xs={24} lg={isAdmin ? 24 : 12}>
            <Card
              title={
                <Space>
                  <MedicineBoxOutlined />
                  <span>Rezultati za pregled</span>
                  {counts?.awaitingReview ? (
                    <Badge count={counts.awaitingReview} style={{ backgroundColor: '#faad14' }} />
                  ) : null}
                </Space>
              }
              extra={
                <Link to="/lab-results">
                  <Button type="link" icon={<ArrowRightOutlined />}>
                    Pogledaj sve
                  </Button>
                </Link>
              }
            >
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <Spin />
                </div>
              ) : doctorQueue.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Nema rezultata za pregled"
                />
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={doctorQueue}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Link to={`/lab-results/${item.id}`} key="review">
                          <Button type="primary" size="small">
                            Pregledaj
                          </Button>
                        </Link>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            src={item.user.image}
                            icon={!item.user.image && <UserOutlined />}
                            style={{ backgroundColor: '#faad14' }}
                          />
                        }
                        title={
                          <Link to={`/lab-results/${item.id}`}>
                            {getPatientName(item.user)}
                          </Link>
                        }
                        description={
                          <Space size="small">
                            <Text type="secondary">{item.labName}</Text>
                            <Text type="secondary">•</Text>
                            <Text type="secondary">{formatDate(item.sampleDate)}</Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </Col>
        )}

        {/* Doctor Section: Upcoming Consultations */}
        {isDoctor && (
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <CalendarOutlined />
                  <span>Predstojeće konsultacije</span>
                  {doctorAppointments.length > 0 && (
                    <Badge count={doctorAppointments.length} style={{ backgroundColor: '#722ed1' }} />
                  )}
                </Space>
              }
              extra={
                <Link to="/my-consultations">
                  <Button type="link" icon={<ArrowRightOutlined />}>
                    Pogledaj sve
                  </Button>
                </Link>
              }
            >
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <Spin />
                </div>
              ) : doctorAppointments.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Nema zakazanih konsultacija"
                />
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={doctorAppointments}
                  renderItem={(apt) => {
                    const timeRemaining = getTimeRemaining(apt.scheduledAt, apt.duration)
                    // Doctor can enter the call from 15 min before scheduled
                    // until 15 min after scheduled end (matches server-side
                    // join window in AppointmentService). Button links to the
                    // in-app call page, not the raw Daily URL, so the server
                    // can mint a scoped meeting token and track join state.
                    const canJoinCall = timeRemaining.state === 'live'
                    return (
                      <List.Item
                        actions={[
                          <Link
                            key="join"
                            to={canJoinCall ? `/my-consultations/${apt.id}/call` : `/my-consultations/${apt.id}`}
                          >
                            <Button
                              type="primary"
                              size="small"
                              disabled={timeRemaining.state === 'past'}
                            >
                              Video poziv
                            </Button>
                          </Link>,
                          <Link to={`/my-consultations/${apt.id}`} key="details">
                            <Button size="small">Detalji</Button>
                          </Link>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              src={apt.patientImage}
                              icon={!apt.patientImage && <UserOutlined />}
                              style={{ backgroundColor: '#722ed1' }}
                            />
                          }
                          title={
                            <Space>
                              <span>{apt.patientName}</span>
                              <Tag color={timeRemaining.color}>{timeRemaining.text}</Tag>
                            </Space>
                          }
                          description={
                            <Space size="small">
                              <CalendarOutlined style={{ color: '#8c8c8c' }} />
                              <Text type="secondary">{formatDateTime(apt.scheduledAt)}</Text>
                              <Text type="secondary">•</Text>
                              <Text type="secondary">{apt.duration} min</Text>
                            </Space>
                          }
                        />
                      </List.Item>
                    )
                  }}
                />
              )}
            </Card>
          </Col>
        )}
      </Row>

      {/* Help Section */}
      <div style={{ marginTop: 24 }}>
        <Alert
          message="Saveti za efikasan rad"
          description={
            isNurse ? (
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Verifikujte lab rezultate u roku od 48h od prijema</li>
                <li>Proverite da li su sve vrednosti ispravno unete pre verifikacije</li>
                <li>Kontaktirajte pacijente telefonom za zakazivanje konsultacija</li>
              </ul>
            ) : isDoctor ? (
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Pregledajte dodeljene rezultate u roku od 48h</li>
                <li>Ostavite detaljne beleške za pacijenta</li>
                <li>Konsultacije se održavaju putem video poziva</li>
              </ul>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Pratite sve redove i eskalacije</li>
                <li>Proveravajte da li ima zaostalih zadataka</li>
              </ul>
            )
          }
          type="info"
          showIcon
        />
      </div>

      {/* Schedule Consultation Modal */}
      <ScheduleConsultationModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSuccess={() => {
          setShowScheduleModal(false)
          // Optionally refresh data
        }}
      />
    </div>
  )
}
